/**
 * The leaf field components.
 *
 * Each takes the same props so `field.resolver` can pick between them without
 * knowing anything about the specific field — which is what keeps the renderer
 * connector-agnostic.
 */

import type { ReactNode } from 'react'
import type { JsonSchema } from '@/core/types/api.types'
import { hints } from '@/core/utils/schema.utils'

export interface FieldProps {
  name: string
  schema: JsonSchema
  value: unknown
  onChange: (value: unknown) => void
  error?: string
  required?: boolean
  disabled?: boolean
}

export function FieldShell({
  name,
  schema,
  error,
  required,
  children,
  labelOverride,
}: FieldProps & { children: ReactNode; labelOverride?: string }) {
  const ui = hints(schema)
  const help = ui.help ?? schema.description
  const id = `field-${name}`

  return (
    <div className="field">
      <label className="field-label" htmlFor={id}>
        {labelOverride ?? ui.label ?? schema.title ?? name}
        {required && (
          <span className="field-req" aria-label="required">
            *
          </span>
        )}
      </label>
      {children}
      {help && !error && <p className="field-help">{help}</p>}
      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}

// --- text -------------------------------------------------------------------

export function TextField(props: FieldProps) {
  const { name, schema, value, onChange, error, disabled } = props
  const ui = hints(schema)
  const multiline = ui.multiline || ui.widget === 'textarea'
  const id = `field-${name}`
  const common = {
    id,
    value: typeof value === 'string' ? value : value == null ? '' : String(value),
    placeholder: ui.placeholder ?? undefined,
    disabled,
    'aria-invalid': error ? true : undefined,
    onChange: (e: { target: { value: string } }) => onChange(e.target.value),
  }

  return (
    <FieldShell {...props}>
      {multiline ? (
        <textarea className="textarea" rows={4} {...common} />
      ) : (
        <input
          className="input"
          type={
            ui.widget === 'password'
              ? 'password'
              : schema.format === 'uri'
                ? 'url'
                : schema.format === 'email'
                  ? 'email'
                  : 'text'
          }
          {...common}
        />
      )}
    </FieldShell>
  )
}

// --- number -----------------------------------------------------------------

export function NumberField(props: FieldProps) {
  const { name, schema, value, onChange, error, disabled } = props
  const ui = hints(schema)
  const isInt = schema.type === 'integer'

  return (
    <FieldShell {...props}>
      <input
        id={`field-${name}`}
        className="input"
        type="number"
        step={isInt ? 1 : 'any'}
        min={schema.minimum}
        max={schema.maximum}
        placeholder={ui.placeholder ?? undefined}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        value={value === undefined || value === null ? '' : String(value)}
        onChange={(e) => {
          const raw = e.target.value
          // An empty box means "unset", not 0 — sending 0 would be a silent lie.
          if (raw === '') return onChange(undefined)
          const parsed = isInt ? Number.parseInt(raw, 10) : Number.parseFloat(raw)
          onChange(Number.isNaN(parsed) ? undefined : parsed)
        }}
      />
    </FieldShell>
  )
}

// --- boolean ----------------------------------------------------------------

export function BooleanField(props: FieldProps) {
  const { name, schema, value, onChange, disabled, error } = props
  const ui = hints(schema)
  const help = ui.help ?? schema.description

  return (
    <div className="field">
      <label className="checkbox-row" htmlFor={`field-${name}`}>
        <input
          id={`field-${name}`}
          type="checkbox"
          checked={value === true}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span>
          <span className="field-label">{ui.label ?? schema.title ?? name}</span>
          {help && <p className="field-help">{help}</p>}
        </span>
      </label>
      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}

// --- select (static enum) ---------------------------------------------------

export function SelectField(props: FieldProps) {
  const { name, schema, value, onChange, error, disabled, required } = props
  const options = schema.enum ?? []

  return (
    <FieldShell {...props}>
      <select
        id={`field-${name}`}
        className="select"
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        value={value === undefined || value === null ? '' : String(value)}
        onChange={(e) => {
          const raw = e.target.value
          if (raw === '') return onChange(undefined)
          // Match back to the original option so numbers stay numbers.
          const found = options.find((o) => String(o) === raw)
          onChange(found ?? raw)
        }}
      >
        <option value="">{required ? 'Select…' : '(not set)'}</option>
        {options.map((opt) => (
          <option key={String(opt)} value={String(opt)}>
            {String(opt)}
          </option>
        ))}
      </select>
    </FieldShell>
  )
}

// --- multi-select (array of enum) -------------------------------------------

export function MultiSelectField(props: FieldProps) {
  const { schema, value, onChange, disabled } = props
  const options = schema.items?.enum ?? []
  const selected = Array.isArray(value) ? value : []

  const toggle = (opt: unknown) => {
    const on = selected.some((s) => s === opt)
    onChange(on ? selected.filter((s) => s !== opt) : [...selected, opt])
  }

  return (
    <FieldShell {...props}>
      <div className="multiselect">
        {options.length === 0 && <span className="faint small">No options declared.</span>}
        {options.map((opt) => (
          <button
            type="button"
            key={String(opt)}
            className="chip"
            data-on={selected.some((s) => s === opt)}
            disabled={disabled}
            onClick={() => toggle(opt)}
          >
            {String(opt)}
          </button>
        ))}
      </div>
    </FieldShell>
  )
}

// --- date -------------------------------------------------------------------

export function DateField(props: FieldProps) {
  const { name, schema, value, onChange, error, disabled } = props
  const isDateTime = schema.format === 'date-time'

  return (
    <FieldShell {...props}>
      <input
        id={`field-${name}`}
        className="input"
        type={isDateTime ? 'datetime-local' : 'date'}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        // datetime-local wants `YYYY-MM-DDTHH:mm`; an ISO string with a zone
        // suffix silently renders blank, so trim it for display only.
        value={typeof value === 'string' ? value.slice(0, isDateTime ? 16 : 10) : ''}
        onChange={(e) => {
          const raw = e.target.value
          if (!raw) return onChange(undefined)
          onChange(isDateTime ? new Date(raw).toISOString() : raw)
        }}
      />
    </FieldShell>
  )
}

// --- raw JSON ---------------------------------------------------------------

export function JsonField(props: FieldProps) {
  const { name, schema, value, onChange, error, disabled } = props
  const ui = hints(schema)
  // Kept as text while typing: reformatting mid-edit fights the user, and
  // half-typed JSON is not parseable anyway.
  const text =
    value === undefined || value === null
      ? ''
      : typeof value === 'string'
        ? value
        : JSON.stringify(value, null, 2)

  let parseError: string | undefined
  if (typeof value === 'string' && value.trim()) {
    try {
      JSON.parse(value)
    } catch {
      parseError = 'Not valid JSON yet.'
    }
  }

  return (
    <FieldShell {...props} error={error ?? parseError}>
      <textarea
        id={`field-${name}`}
        className="textarea textarea-mono"
        rows={6}
        spellCheck={false}
        disabled={disabled}
        placeholder={ui.placeholder ?? '{\n  "key": "value"\n}'}
        aria-invalid={error || parseError ? true : undefined}
        value={text}
        onChange={(e) => {
          const raw = e.target.value
          if (!raw.trim()) return onChange(undefined)
          try {
            onChange(JSON.parse(raw))
          } catch {
            onChange(raw)
          }
        }}
      />
    </FieldShell>
  )
}
